import { SessionRefreshFailedError } from "./errors";
import { isTokenExpiringSoon } from "./jwtExpiration";
import { readSessionToken, writeSessionToken } from "./sessionCookie";

const THREE_MINUTES_IN_MS = 3 * 60 * 1000;
const REFRESH_TIMEOUT_IN_MS = 10_000;

let inFlightRefresh: Promise<string> | null = null;

const refreshTimeoutSignal = (): AbortSignal | undefined =>
  typeof AbortSignal.timeout === "function"
    ? AbortSignal.timeout(REFRESH_TIMEOUT_IN_MS)
    : undefined;

const requestNewToken = async (staleToken: string): Promise<string> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_PORTAIL_API_URL}/auth/refresh`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${staleToken}`,
        Accept: "application/json",
      },
      signal: refreshTimeoutSignal(),
    },
  );

  if (!response.ok) {
    throw new SessionRefreshFailedError(`portal answered ${response.status}`);
  }

  const data = await response.json();
  const newToken = data?.access_token;

  if (typeof newToken !== "string" || newToken === "") {
    throw new SessionRefreshFailedError("portal answered without a token");
  }

  return newToken;
};

const performRefresh = async (staleToken: string): Promise<string> => {
  // Another tab (or the portal itself) may already have refreshed the shared
  // cookie: reuse it instead of presenting a token the portal has blacklisted.
  const currentToken = readSessionToken();
  if (
    currentToken &&
    currentToken !== staleToken &&
    !isTokenExpiringSoon(currentToken, THREE_MINUTES_IN_MS)
  ) {
    return currentToken;
  }

  const newToken = await requestNewToken(staleToken);
  writeSessionToken(newToken);

  return newToken;
};

export const refreshSessionToken = (staleToken: string): Promise<string> => {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = performRefresh(staleToken).finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};

export const resolveRequestToken = async (): Promise<string | undefined> => {
  const token = readSessionToken();
  if (!token) return undefined;
  if (!isTokenExpiringSoon(token, THREE_MINUTES_IN_MS)) return token;

  return refreshSessionToken(token);
};
